import type { MeasurementStrategyContext, PdpMeasurementStrategy } from './PdpMeasurementStrategy';

const SPATIAL_ROOT_SELECTOR =
  '.product-detail-media [data-spatial-gallery-slider-viewer], .product-detail-media [data-spatial-zoom-gallery-slider-viewer], .gallery-slider [data-spatial-gallery-slider-viewer], .gallery-slider [data-spatial-zoom-gallery-slider-viewer]';

const DRAG_DISTANCE_PX = 140;

const MIN_MS_BETWEEN_VIEWED = 450;

interface TrackedPointer {
  id: number;
  lastX: number;
  lastY: number;
  accum: number;
}

/**
 * Spatial / third-party 360 viewers (separate from tiny-slider): Shopware’s spatial canvas and similar
 * plugins rarely expose a stable “frame index” in DOM, so we approximate engagement from throttled drag
 * distance on known viewer roots instead of duplicating `GallerySlideViewedStrategy` heuristics.
 *
 * Plugin coverage: yes — Shopware storefront spatial gallery / zoom viewer (`data-spatial-gallery-slider-viewer`,
 * `data-spatial-zoom-gallery-slider-viewer`).
 * Third-party 360 extensions (e.g. moori “Responsive 360 degree viewer”, bogx “360 Degrees Viewer”, or
 * Ajax-Zoom-style embeds) only if they expose matching roots — extend `SPATIAL_ROOT_SELECTOR` per vendor DOM.
 */
export class ThreeSixtyGalleryStrategy implements PdpMeasurementStrategy {
  private ctx: MeasurementStrategyContext | null = null;

  private roots: HTMLElement[] = [];

  private tracked: TrackedPointer | null = null;

  private lastViewedAtMs = 0;

  private onPointerDown: ((ev: Event) => void) | null = null;

  private onPointerMove: ((ev: Event) => void) | null = null;

  private onPointerUp: ((ev: Event) => void) | null = null;

  private maybeIncrementViewed(): void {
    if (!this.ctx) {
      return;
    }
    const now = this.ctx.window.performance?.now?.() ?? Date.now();
    if (now - this.lastViewedAtMs < MIN_MS_BETWEEN_VIEWED) {
      return;
    }
    this.lastViewedAtMs = now;
    this.ctx.store.incrementImageViewed();
  }

  attach(ctx: MeasurementStrategyContext): void {
    this.ctx = ctx;
    const { document: doc, events } = ctx;

    this.roots = [...doc.querySelectorAll<HTMLElement>(SPATIAL_ROOT_SELECTOR)];

    if (this.roots.length === 0) {
      return;
    }

    this.onPointerDown = (ev: Event) => {
      const p = ev as PointerEvent;
      if (p.button !== 0) {
        return;
      }
      const root = ev.currentTarget as HTMLElement | null;
      if (!root || !this.roots.includes(root)) {
        return;
      }
      this.tracked = { id: p.pointerId, lastX: p.clientX, lastY: p.clientY, accum: 0 };
    };

    this.onPointerMove = (ev: Event) => {
      const p = ev as PointerEvent;
      if (p.pointerId !== this.tracked?.id) {
        return;
      }
      const dx = p.clientX - this.tracked.lastX;
      const dy = p.clientY - this.tracked.lastY;
      this.tracked.lastX = p.clientX;
      this.tracked.lastY = p.clientY;
      this.tracked.accum += Math.hypot(dx, dy);
      if (this.tracked.accum >= DRAG_DISTANCE_PX) {
        this.tracked.accum = 0;
        this.maybeIncrementViewed();
      }
    };

    this.onPointerUp = (ev: Event) => {
      const p = ev as PointerEvent;
      if (p.pointerId === this.tracked?.id) {
        this.tracked = null;
      }
    };

    for (const root of this.roots) {
      events.on(root, 'pointerdown', this.onPointerDown);
      events.on(root, 'pointermove', this.onPointerMove, { passive: true });
      events.on(root, 'pointerup', this.onPointerUp);
      events.on(root, 'pointercancel', this.onPointerUp);
    }
  }

  dispose(): void {
    if (this.ctx != null && this.onPointerDown != null && this.onPointerMove != null && this.onPointerUp != null) {
      const { events } = this.ctx;
      for (const root of this.roots) {
        events.off(root, 'pointerdown', this.onPointerDown);
        events.off(root, 'pointermove', this.onPointerMove, { passive: true });
        events.off(root, 'pointerup', this.onPointerUp);
        events.off(root, 'pointercancel', this.onPointerUp);
      }
    }
    this.roots = [];
    this.onPointerDown = null;
    this.onPointerMove = null;
    this.onPointerUp = null;
    this.tracked = null;
    this.ctx = null;
  }
}
